import Box from "@cloudscape-design/components/box"
import FormField from "@cloudscape-design/components/form-field"
import Link from "@cloudscape-design/components/link"
import Select, { type SelectProps } from "@cloudscape-design/components/select"

import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { MetadataField } from "./useMetadataFields"

/**
 * Props for the FieldSelect component.
 */
interface FieldSelectProps {
  /**
   * Currently selected field name.
   */
  value: string | undefined | null

  /**
   * Callback when selection changes.
   */
  onChange: (value: string) => void

  /**
   * Available fields from metadata.
   */
  availableFields: MetadataField[]

  /**
   * Whether the input is disabled.
   */
  disabled?: boolean

  /**
   * Placeholder text when no selection.
   */
  placeholder?: string

  /**
   * Whether the field has a validation error.
   */
  hasError?: boolean

  /**
   * When true, render the selected field as plain text instead of a (disabled) dropdown (#709).
   */
  readOnly?: boolean

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
}

/**
 * Single-select dropdown for choosing a field from metadata. Shows field name in dropdown, label as
 * description on hover. Displays empty state with link to Metadata tab when no fields available.
 */
export const FieldSelect: React.FC<FieldSelectProps> = ({
  value,
  onChange,
  availableFields,
  disabled,
  placeholder = "Select field",
  hasError,
  readOnly,
  onNavigateToMetadata,
}) => {
  if (readOnly) {
    return <ReadOnlyValue value={value} />
  }

  const isEmpty = availableFields.length === 0

  if (isEmpty) {
    return (
      <FormField
        constraintText={
          <Box color="text-status-warning" fontSize="body-s">
            No fields defined.{" "}
            {onNavigateToMetadata && (
              <Link variant="primary" onFollow={onNavigateToMetadata}>
                Add fields in the Metadata tab →
              </Link>
            )}
          </Box>
        }
      >
        <Select
          disabled
          selectedOption={null}
          onChange={() => {}}
          options={[]}
          placeholder="No fields available"
        />
      </FormField>
    )
  }

  const selectedField = availableFields.find((f) => f.name === value)
  const isOrphaned = !!value && !selectedField

  const options: SelectProps.Option[] = availableFields.map((field) => ({
    value: field.name,
    label: field.name,
    description: field.label,
  }))

  const selectedOption: SelectProps.Option | null = value
    ? isOrphaned
      ? { value, label: `${value} (not in metadata)` }
      : { value, label: value, description: selectedField?.label }
    : null

  const select = (
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => onChange(detail.selectedOption.value ?? "")}
      options={options}
      disabled={disabled}
      placeholder={placeholder}
      invalid={hasError || isOrphaned}
      filteringType="auto"
    />
  )

  // Only wrap in a FormField when there's an orphan error to surface; otherwise let the
  // caller's FormField own the label/description so FormFields don't nest.
  if (isOrphaned) {
    return <FormField errorText={`Field "${value}" not found in Metadata`}>{select}</FormField>
  }

  return select
}
